<script setup>
import MaterialInfoFields from "./MaterialInfoFields.vue";
import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref, watch } from "vue";
import { useUiStore } from "@/app/stores/uiStore";


import MaterialService from "@/api/services/material.service";

// eslint-disable-next-line no-undef
const emit = defineEmits(["closeDialog", "fulfillRequest"]);

// eslint-disable-next-line no-undef
const props = defineProps({
  request: {
    type: Object,
    default: null,
  },
});

const requestStatusLabel = computed(() => ({
  pending: "Pendiente", approved: "Aprobada", rejected: "Rechazada",
  fulfilled: "Completada", fullfilled: "Completada",
})[props.request?.status || "pending"] || "Pendiente");

const uiStore = useUiStore();
const materials = ref([]);
const form = ref({
  material: null,
  assignedAt: "",
  notes: "",
});

const filteredMaterials = computed(() => {
  const requestTypeIri = props.request?.materialType?.["@id"];

  return materials.value.filter((material) => {
    const sameType = !requestTypeIri || material.type?.["@id"] === requestTypeIri;
    const isEmployee = material.ownerType === "employee";
    const isAvailable = material.status === "available";
    return sameType && isEmployee && isAvailable;
  });
});

function pad(number) {
  return String(number).padStart(2, "0");
}

function formatDateTimeForPicker(date = new Date()) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

onMounted(() => {
  form.value.assignedAt = formatDateTimeForPicker();
  form.value.notes = props.request?.systemsComment || "";
  getMaterials();
});

watch(
  () => props.request,
  (value) => {
    form.value.notes = value?.systemsComment || "";
    if (!form.value.assignedAt) {
      form.value.assignedAt = formatDateTimeForPicker();
    }
  },
  { immediate: true }
);

const closeDialog = () => {
  emit("closeDialog");
};

const saveEntry = () => {
  if (!form.value.material || !form.value.assignedAt) {
    notification.addNotification({
      type: "error",
      message: "Tienes que seleccionar un material e indicar la fecha de entrega.",
      duration: 5000,
    });
    return;
  }

  emit("fulfillRequest", {
    material: form.value.material,
    assignedAt: form.value.assignedAt,
    notes: form.value.notes || "",
  });
};

const getMaterials = () => {
  const requestTypeId = props.request?.materialType?.id || extractId(props.request?.materialType?.["@id"]);
  const params = {
    page: 1,
    itemsPerPage: 99999,
    status: "available",
    ownerType: "employee",
    ...(requestTypeId ? { "type.id": requestTypeId } : {}),
  };

  uiStore.startLoading();
  MaterialService.getMaterials(params)
    .then((res) => {
      if (res && res.status === 200) {
        materials.value = res.data["hydra:member"];
      }
      uiStore.stopLoading();
    })
    .catch(() => {
      uiStore.stopLoading();
    });
};

const extractId = (iri) => {
  if (!iri) {
    return null;
  }

  const parts = iri.split("/");
  return Number(parts[parts.length - 1]);
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
</script>

<template>
  <div>
    <div class="header">
      <img style="width: 30px; margin-bottom: 14px;" src="@/assets/icons/box-blue.svg">
      <span class="header-title pl-4">
        Completar solicitud
      </span>
    </div>

    <div class="row f-flex" v-if="request">
      <div class="col-6 el-col-xs-24 mt-3">
        <label class="w-100">Fecha de solicitud</label>
        <el-input :model-value="formatDateTime(request.requestedAt)" disabled />
      </div>
      <div class="col-6 el-col-xs-24 mt-3">
        <label class="w-100">Empleado</label>
        <el-input :model-value="request.employee?.fullName || ''" disabled />
      </div>
      <div class="col-6 el-col-xs-24 mt-3">
        <label class="w-100">Tipo de material</label>
        <el-input :model-value="request.materialType?.name || ''" disabled />
      </div>
      <div class="col-6 el-col-xs-24 mt-3">
        <label class="w-100">Estado</label>
        <el-input :model-value="requestStatusLabel" disabled class="w-100" />
      </div>
      <div class="col-12 el-col-xs-24 mt-3">
        <label class="w-100">Descripción material</label>
        <el-input :model-value="request.description || ''" type="textarea" :rows="4" disabled class="custom-textarea" />
      </div>

      <div class="col-12 el-col-xs-24 mt-3">
        <label class="w-100">Comentario de sistemas</label>
        <el-input :model-value="request.systemsComment || ''" type="textarea" :rows="4" disabled class="w-100" />
      </div>
      <div class="col-6 el-col-xs-24 mt-3">
        <label for="fulfillAssignedAt" class="w-100">Fecha de entrega</label>
        <el-date-picker popper-class="material-modal-picker" id="fulfillAssignedAt" v-model="form.assignedAt" type="datetime" class="w-100"
          placeholder="Fecha entrega" format="DD/MM/YYYY HH:mm" value-format="YYYY-MM-DD HH:mm" />
      </div>
      <div class="col-6 el-col-xs-24 mt-3">
        <label for="fulfillMaterial" class="w-100">Número de serie</label>
        <el-select popper-class="material-modal-select" id="fulfillMaterial" v-model="form.material" filterable clearable
          placeholder="Seleccione numero de serie del material" :value-key="'@id'">
          <el-option v-for="material in filteredMaterials" :key="material.id" :label="material.serialTag"
            :value="material" />
        </el-select>
      </div>

      <div class="col-12" v-if="form.material">
        <MaterialInfoFields :material="form.material" :show-identification="false" />
      </div>

      <div class="col-12 el-col-xs-24 mt-3">
        <label for="fulfillNotes" class="w-100">Notas</label>
        <el-input id="fulfillNotes" v-model="form.notes" type="textarea" :rows="4" placeholder="Escribe tus comentarios"
          class="custom-textarea" />
      </div>
    </div>

    <div class="row footer">
      <div class="col-md-12 mt-4 footer-container">
        <div class="d-flex justify-content-end gap-3">
          <div class="col-md-3">
            <button class="btn btn-block btn_yellow edit-button material-request-button" @click="closeDialog">
              Cancelar
            </button>
          </div>
          <div class="col-md-3">
            <button type="submit" class="btn btn-block request-action-complete edit-button material-request-button" @click="saveEntry">
              Completar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.header-title {
  font-size: 30px;
  margin-left: 5px;
  font-weight: 800;
  letter-spacing: -0.05em;
  color: #474ce6;
}

.custom-textarea:deep(.el-textarea__inner) {
  font-size: 14px !important;
  border-radius: 20px;
}

.footer-container {
  margin-left: 0;
}

.request-action-complete {
  background-color: #107dde;
  color: #fff;
}

.request-action-complete:hover,
.request-action-complete:active {
  color: #fff;
  filter: brightness(0.9);
}

.request-action-complete:focus-visible {
  color: #fff;
  outline: 2px solid #409eff;
  outline-offset: 2px;
}

@media (max-width: 767.98px) {
  .footer-container {
    margin-left: 0 !important;
  }

  .row {
    margin-left: 0;
    margin-right: 0;
  }

  [class*="col-"] {
    padding-left: 0;
    padding-right: 0;
  }

  .footer {
    .d-flex {
      flex-direction: column;
      gap: 0.75rem !important;
    }

    .col-md-3 {
      width: 100%;
    }

    .edit-button {
      width: 100%;
    }
  }
}

@media (max-width: 500px) {
  .header-title {
    font-size: 25px;
  }
}
@import "./materialRequestButtons";
</style>
